/**
 * Cross-portal origin helpers (Phase 4 — subdomain split).
 *
 * The learner app is served from the bare host (e.g.
 * `tutara-prototype.trigear.in`) and the admin console from the `admin.`
 * subdomain (`admin.tutara-prototype.trigear.in`). Both are the same single
 * SPA image — the subdomain only decides the default landing shell — but
 * because React Router `<Link>`/`<NavLink>` cannot cross origins, cross-portal
 * navigation must use absolute URLs in production.
 *
 * On localhost (one origin, path-based `/admin`) these helpers collapse to
 * same-origin paths so local dev keeps working unchanged. Callers use the
 * returned `external` flag to decide between an `<a href>` (cross-origin) and a
 * React Router `<Link>` (same-origin SPA navigation).
 */

const ADMIN_PREFIX = 'admin.';

function currentHost(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hostname;
}

/**
 * True on localhost / bare IPs where there is no shared parent domain to split
 * on — the admin console then lives at the same origin under `/admin`.
 */
export function isSingleOriginHost(host = currentHost()): boolean {
  return host === 'localhost' || host === '127.0.0.1' || host === '' || !host.includes('.');
}

/** True when the current page is being served from the `admin.` subdomain. */
export function isAdminHost(host = currentHost()): boolean {
  return host.startsWith(ADMIN_PREFIX);
}

/** Bare learner host for a given (possibly admin) host. */
function learnerHost(host = currentHost()): string {
  return isAdminHost(host) ? host.slice(ADMIN_PREFIX.length) : host;
}

/** Admin subdomain host for a given (possibly bare) host. */
function adminHost(host = currentHost()): string {
  return isAdminHost(host) ? host : ADMIN_PREFIX + host;
}

function originFor(host: string): string {
  const { protocol, port } = window.location;
  const portPart = port ? `:${port}` : '';
  return `${protocol}//${host}${portPart}`;
}

export interface PortalTarget {
  /** Absolute URL when crossing origins, otherwise a same-origin path. */
  href: string;
  /** True when `href` crosses origins and must be used with `<a>`, not `<Link>`. */
  external: boolean;
}

/**
 * Resolve a path in the LEARNER portal. Returns a same-origin path on localhost
 * or when already on the learner host; an absolute cross-subdomain URL when
 * called from the admin subdomain.
 */
export function learnerUrl(path = '/'): PortalTarget {
  if (isSingleOriginHost()) return { href: path, external: false };
  const host = learnerHost();
  if (host === currentHost()) return { href: path, external: false };
  return { href: originFor(host) + path, external: true };
}

/**
 * Resolve a path in the ADMIN portal. Returns a same-origin path on localhost
 * or when already on the admin subdomain; an absolute cross-subdomain URL when
 * called from the learner host.
 */
export function adminUrl(path = '/admin'): PortalTarget {
  if (isSingleOriginHost()) return { href: path, external: false };
  const host = adminHost();
  if (host === currentHost()) return { href: path, external: false };
  return { href: originFor(host) + path, external: true };
}
